import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { getRuntimeConfig } from '@/shared/config/runtime'
import type { LithosConsent } from '@/shared/lib/lithos-consent'
import { Button } from '@/shared/ui/button'

/**
 * The shell's footer: the suite's legal pages and the cookie preferences.
 *
 * The realm serves both the legal pages and the consent banner, so with no realm
 * there is nothing to link. Cookie settings appears only once the banner has
 * loaded, since it is what the button opens.
 */
export function AppFooter({ consent }: { consent: LithosConsent | null }) {
  const { t } = useTranslation()
  const issuer = getRuntimeConfig().idpIssuer

  if (issuer === '') return null
  const origin = new URL(issuer).origin

  const items = [
    { label: t('footer.privacyPolicy'), href: `${origin}/privacy-policy` },
    { label: t('footer.termsOfService'), href: `${origin}/terms-of-service` },
    ...(consent !== null
      ? [{ label: t('action.cookieSettings'), onClick: () => consent.openPreferences() }]
      : []),
  ]

  return (
    // A labelled nav rather than a footer: inside the shell's `<main>` a footer
    // is no landmark at all.
    <nav
      aria-label={t('footer.label')}
      className="flex shrink-0 items-center justify-center text-muted-foreground"
    >
      {items.map((item, index) => (
        <Fragment key={item.label}>
          {index > 0 && <span aria-hidden>|</span>}
          {'href' in item ? (
            <Button asChild variant="link" size="sm" className="text-muted-foreground">
              <a href={item.href} target="_blank" rel="noopener noreferrer">
                {item.label}
              </a>
            </Button>
          ) : (
            <Button
              variant="link"
              size="sm"
              className="text-muted-foreground"
              onClick={item.onClick}
            >
              {item.label}
            </Button>
          )}
        </Fragment>
      ))}
    </nav>
  )
}
